import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import settingsService from '../../../../services/settings.service';
import { confirmDelete, showError, showSuccess } from '../../../../utils/ecxSwal';
import { ClinicInfoPanel } from './info/clinicInfoParts';
import ClinicSettingsSectionHeader from './subparts/ClinicSettingsSectionHeader';
import ClinicSettingsEmpty from './subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from './subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from './subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from './subparts/ClinicSettingsListSkeleton';

const socialLinkTypes = [
    { value: 'WEBSITE', label: 'Website' },
    { value: 'GOOGLE_PAGE', label: 'Google Business' },
    { value: 'FACEBOOK', label: 'Facebook' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'TWITTER', label: 'Twitter' },
    { value: 'YELP', label: 'Yelp' },
    { value: 'OTHER', label: 'Other' },
];

const getPlatformIcon = (type) => {
    const icons = {
        WEBSITE: 'bi-globe',
        GOOGLE_PAGE: 'bi-google',
        FACEBOOK: 'bi-facebook',
        INSTAGRAM: 'bi-instagram',
        TWITTER: 'bi-twitter',
        YELP: 'bi-star-fill',
        OTHER: 'bi-link-45deg',
    };
    return icons[type] || 'bi-link-45deg';
};

const getSocialLinkLabel = (type) => {
    const socialType = socialLinkTypes.find((st) => st.value === type);
    return socialType ? socialType.label : type;
};

const SocialLinks = () => {
    const [socialLinks, setSocialLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const fetchSocialLinks = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getSocialLinks(clinicId);
            setSocialLinks(response.data);
        } catch (error) {
            console.error('Error fetching social links:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load social links. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const addSocialLink = async (data) => {
        setIsSaving(true);
        try {
            const socialLinkData = {
                type: data.type,
                link: data.link,
                clinicId,
            };
            const response = await settingsService.addUpdateSocialLinks(clinicId, socialLinkData);
            setSocialLinks((prev) => [...prev, response.data]);
            setAddingNew(false);
            reset();
            await showSuccess({
                title: 'Link Added',
                text: 'The social link was added successfully.',
            });
        } catch (error) {
            console.error('Error adding social link:', error);
            showError({
                title: 'Add Failed',
                text: 'Unable to add the social link. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateSocialLink = async (socialLinkId, data) => {
        setIsSaving(true);
        try {
            const updatedSocialLinkData = {
                id: socialLinkId,
                type: data.type,
                link: data.link,
                clinicId,
            };
            const response = await settingsService.addUpdateSocialLinks(clinicId, updatedSocialLinkData);
            setSocialLinks((prev) =>
                prev.map((socialLink) =>
                    socialLink.id === socialLinkId
                        ? { ...socialLink, ...response.data, type: data.type, link: data.link }
                        : socialLink
                )
            );
            setEditingId(null);
            reset();
            await showSuccess({
                title: 'Link Updated',
                text: 'The social link was updated successfully.',
            });
        } catch (error) {
            console.error('Error updating social link:', error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update the social link. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteSocialLink = async (socialLink) => {
        const result = await confirmDelete({
            title: 'Delete Link?',
            itemName: getSocialLinkLabel(socialLink.type),
            description: 'This social link will be removed from your clinic profile.',
            onConfirm: () => settingsService.deleteSocialLink(clinicId, socialLink.id),
        });

        if (result.isConfirmed) {
            setSocialLinks((prev) => prev.filter((item) => item.id !== socialLink.id));
        }
    };

    const handleEdit = (socialLink) => {
        setEditingId(socialLink.id);
        setAddingNew(false);
        setValue('type', socialLink.type);
        setValue('link', socialLink.link);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ type: 'WEBSITE', link: '' });
    };

    useEffect(() => {
        fetchSocialLinks();
    }, [clinicId]);

    const renderLinkForm = (onSubmit, submitLabel) => (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-settings-record-form">
            <div className="clinic-settings-record-form__row">
                <div>
                    <label className="form-label small fw-normal text-muted">Platform</label>
                    <select
                        className={`form-select form-select-sm ${errors.type ? 'is-invalid' : ''}`}
                        {...register('type', { required: 'Platform type is required' })}
                    >
                        {socialLinkTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                    {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                </div>
                <div>
                    <label className="form-label small fw-normal text-muted">URL</label>
                    <input
                        type="url"
                        className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                        placeholder="https://example.com"
                        {...register('link', {
                            required: 'Link is required',
                            pattern: {
                                value: /^https?:\/\/.+\..+/,
                                message: 'Please enter a valid URL starting with http:// or https://',
                            },
                        })}
                    />
                    {errors.link && <div className="invalid-feedback">{errors.link.message}</div>}
                </div>
            </div>
            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel={submitLabel} />
        </form>
    );

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Social Links"
                icon="bi-share"
                subtitle="Manage your clinic's social media and website links"
                actions={
                    !loading ? (
                        addingNew ? (
                            <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
                        ) : (
                            <button className="btn btn-sm btn-light-primary rounded-pill" onClick={handleAddNew} type="button">
                                <i className="bi bi-plus-circle me-1"></i>
                                Add New Link
                            </button>
                        )
                    ) : null
                }
            />

            {loading ? (
                <ClinicInfoPanel title="Links" icon="bi-link-45deg">
                    <ClinicSettingsListSkeleton rows={4} />
                </ClinicInfoPanel>
            ) : (
                <ClinicInfoPanel
                    title="Links"
                    icon="bi-link-45deg"
                    actions={editingId ? <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} /> : null}
                >
                    {addingNew && renderLinkForm(addSocialLink, 'Add Link')}

                    {socialLinks.length === 0 && !addingNew ? (
                        <ClinicSettingsEmpty
                            icon="bi-link-45deg"
                            title="No social links found"
                            description="Get started by adding your first social media or website link."
                            actionLabel="Add First Link"
                            onAction={handleAddNew}
                        />
                    ) : (
                        <div className="clinic-settings-record-list">
                            {socialLinks.map((socialLink) => (
                                <div
                                    key={socialLink.id}
                                    className={`clinic-settings-record-item ${editingId === socialLink.id ? 'clinic-settings-record-item--editing' : ''}`}
                                >
                                    {editingId === socialLink.id ? (
                                        <div className="flex-grow-1">
                                            {renderLinkForm((data) => updateSocialLink(socialLink.id, data), 'Save')}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="clinic-settings-record-item__avatar">
                                                <i className={`bi ${getPlatformIcon(socialLink.type)}`}></i>
                                            </span>
                                            <div className="clinic-settings-record-item__main">
                                                <h6 className="clinic-settings-record-item__title">
                                                    {getSocialLinkLabel(socialLink.type)}
                                                </h6>
                                                <a
                                                    href={socialLink.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="clinic-settings-record-item__link"
                                                >
                                                    {socialLink.link}
                                                    <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                                                </a>
                                            </div>
                                            <div className="clinic-settings-record-item__actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                                    onClick={() => handleEdit(socialLink)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger rounded-circle"
                                                    onClick={() => deleteSocialLink(socialLink)}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ClinicInfoPanel>
            )}
        </>
    );
};

export default SocialLinks;
